/**
 * MirasysPlayer - Unified video player for Mirasys VMS
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import useAuthStore from '../stores/authStore';
import { AlertCircle, Loader2, RefreshCw, Play } from 'lucide-react';

const SEEK_THRESHOLD_MS = 300000;

// Check if data is likely JSON (starts with { and contains "type":)
function isJsonMessage(data) {
  if (data instanceof ArrayBuffer) {
    if (data.byteLength < 10) return false;
    const view = new Uint8Array(data);
    // Must start with '{'
    if (view[0] !== 0x7b) return false;
    // Check for "type" pattern in first 50 bytes
    const checkLen = Math.min(50, view.length);
    const str = String.fromCharCode.apply(null, view.slice(0, checkLen));
    return str.includes('"type"');
  }
  return typeof data === 'string';
}

export default function MirasysPlayer({
  cameraId,
  positionMs = null,
  className = '',
  onPlaying,
  onError,
  muted = true
}) {
  const { currentTenant } = useAuthStore();
  const tenantId = currentTenant?.id || localStorage.getItem('currentTenantId') || 'default';

  const videoRef = useRef(null);
  const wsRef = useRef(null);
  const mediaSourceRef = useRef(null);
  const sourceBufferRef = useRef(null);
  const queueRef = useRef([]);
  const isAppendingRef = useRef(false);
  const hasInitRef = useRef(false);
  const lastConnectParamsRef = useRef(null);
  const frameCountRef = useRef(0);
  
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [needsInteraction, setNeedsInteraction] = useState(false);

  const isArchiveMode = positionMs && positionMs > 0;

  const processQueue = useCallback(() => {
    if (!sourceBufferRef.current || isAppendingRef.current) return;
    if (queueRef.current.length === 0) return;
    if (sourceBufferRef.current.updating) return;

    try {
      isAppendingRef.current = true;
      const chunk = queueRef.current.shift();
      sourceBufferRef.current.appendBuffer(chunk);
    } catch (e) {
      console.error('[MirasysPlayer] appendBuffer error:', e.name);
      isAppendingRef.current = false;
      if (e.name === 'QuotaExceededError' && sourceBufferRef.current?.buffered?.length > 0) {
        try {
          const start = sourceBufferRef.current.buffered.start(0);
          const end = sourceBufferRef.current.buffered.end(0);
          if (end - start > 10) sourceBufferRef.current.remove(start, end - 5);
        } catch (re) {}
      }
    }
  }, []);

  const doCleanup = useCallback(() => {
    console.log('[MirasysPlayer] Cleanup');
    
    if (wsRef.current) {
      try { wsRef.current.close(); } catch(e) {}
      wsRef.current = null;
    }
    queueRef.current = [];
    sourceBufferRef.current = null;
    hasInitRef.current = false;
    frameCountRef.current = 0;
    if (mediaSourceRef.current?.readyState === 'open') {
      try { mediaSourceRef.current.endOfStream(); } catch(e) {}
    }
    mediaSourceRef.current = null;
  }, []);

  const handleUserPlay = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      video.play().then(() => {
        setNeedsInteraction(false);
        setStatus('playing');
      }).catch(e => {
        console.error('[MirasysPlayer] Manual play failed:', e);
      });
    }
  }, []);

  const startConnection = useCallback((camId, archiveMode, startPos) => {
    const params = JSON.stringify({ camId, archiveMode, startPos: archiveMode ? startPos : null });
    if (lastConnectParamsRef.current === params && wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }
    lastConnectParamsRef.current = params;

    doCleanup();
    
    if (!camId) return;
    
    setStatus('connecting');
    setErrorMsg('');
    setNeedsInteraction(false);
    
    const modeStr = archiveMode ? `archive @ ${new Date(startPos).toISOString()}` : 'live';
    console.log(`[MirasysPlayer] Connecting to ${camId} (${modeStr})...`);

    const mediaSource = new MediaSource();
    mediaSourceRef.current = mediaSource;
    
    if (videoRef.current) {
      videoRef.current.src = URL.createObjectURL(mediaSource);
    }

    mediaSource.addEventListener('sourceopen', () => {
      console.log('[MirasysPlayer] MediaSource opened');
      
      try {
        const mimes = [
          'video/mp4; codecs="avc1.640028"',
          'video/mp4; codecs="avc1.64001F"',
          'video/mp4; codecs="avc1.4D401F"',
          'video/mp4; codecs="avc1.42E01E"'
        ];
        const mime = mimes.find(m => MediaSource.isTypeSupported(m));
        if (!mime) throw new Error('No supported MIME type');
        
        console.log('[MirasysPlayer] Using MIME:', mime);
        sourceBufferRef.current = mediaSource.addSourceBuffer(mime);
        sourceBufferRef.current.mode = 'segments';
        
        sourceBufferRef.current.addEventListener('updateend', () => {
          isAppendingRef.current = false;
          processQueue();
          
          // Buffer management
          if (sourceBufferRef.current?.buffered?.length > 0 && !sourceBufferRef.current.updating) {
            const start = sourceBufferRef.current.buffered.start(0);
            const end = sourceBufferRef.current.buffered.end(0);
            if (end - start > 60) {
              try { sourceBufferRef.current.remove(start, end - 30); } catch(e) {}
            }
          }
          
          // Auto-play
          if (!hasInitRef.current && videoRef.current) {
            hasInitRef.current = true;
            
            videoRef.current.play()
              .then(() => {
                console.log('[MirasysPlayer] Playback started');
                setStatus('playing');
                onPlaying?.();
              })
              .catch(e => {
                console.log('[MirasysPlayer] Autoplay blocked:', e.message);
                setStatus('playing');
                setNeedsInteraction(true);
              });
          }
        });
        
        // Connect WebSocket
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const wsUrl = `${protocol}//${window.location.host}/ws/mirasys-gateway?tenantId=${tenantId}`;
        console.log('[MirasysPlayer] WebSocket connecting:', wsUrl);
        
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        ws.binaryType = 'arraybuffer';
        
        ws.onopen = () => {
          console.log('[MirasysPlayer] WebSocket connected');
          setStatus('buffering');
          
          const msg = {
            type: 'subscribe',
            cameraId: camId,
            mode: archiveMode ? 'playback' : 'live',
            width: 1280,
            height: 720
          };
          if (archiveMode && startPos) msg.positionMs = startPos;
          ws.send(JSON.stringify(msg));
        };
        
        ws.onmessage = (event) => {
          // Check if this is a JSON control message
          if (isJsonMessage(event.data)) {
            try {
              const text = typeof event.data === 'string' 
                ? event.data 
                : new TextDecoder().decode(event.data);
              const jsonData = JSON.parse(text);
              
              if (jsonData.type === 'error') {
                console.error('[MirasysPlayer] Server error:', jsonData.message);
                setStatus('error');
                setErrorMsg(jsonData.message);
                onError?.(jsonData.message);
              } else if (jsonData.type === 'subscribed') {
                console.log('[MirasysPlayer] Subscribed:', jsonData.mode);
              } else if (jsonData.type === 'stream-ended') {
                setStatus('ended');
                setErrorMsg(archiveMode ? 'Archive playback complete' : 'Stream ended');
              } else if (jsonData.type === 'connected') {
                console.log('[MirasysPlayer] Gateway connected');
              }
            } catch(e) {
              console.error('[MirasysPlayer] Failed to parse JSON:', e);
            }
          } else if (event.data instanceof ArrayBuffer) {
            // Binary video data
            frameCountRef.current++;
            queueRef.current.push(new Uint8Array(event.data));
            processQueue();
          }
        };
        
        ws.onerror = () => {
          console.error('[MirasysPlayer] WebSocket error');
          setStatus('error');
          setErrorMsg('Connection error');
          onError?.('Connection error');
        };
        
        ws.onclose = (e) => {
          console.log('[MirasysPlayer] WebSocket closed:', e.code);
        };
        
      } catch (e) {
        console.error('[MirasysPlayer] Setup error:', e);
        setStatus('error');
        setErrorMsg('Failed to initialize video player');
      }
    });
  }, [doCleanup, processQueue, onPlaying, onError, tenantId]);

  useEffect(() => {
    const lastParams = lastConnectParamsRef.current ? JSON.parse(lastConnectParamsRef.current) : null;
    const newArchiveMode = isArchiveMode;
    const newPos = newArchiveMode ? positionMs : null;
    
    let shouldConnect = false;
    
    if (!lastParams) {
      shouldConnect = true;
    } else if (lastParams.camId !== cameraId) {
      shouldConnect = true;
    } else if (lastParams.archiveMode !== newArchiveMode) {
      shouldConnect = true;
    } else if (newArchiveMode && lastParams.startPos && newPos) {
      const diff = Math.abs(newPos - lastParams.startPos);
      if (diff > SEEK_THRESHOLD_MS) {
        shouldConnect = true;
      }
    }
    
    if (shouldConnect) {
      startConnection(cameraId, newArchiveMode, newPos);
    }
  }, [cameraId, isArchiveMode, positionMs, startConnection]);

  useEffect(() => {
    return () => {
      doCleanup();
    };
  }, [doCleanup]);

  const retry = () => {
    lastConnectParamsRef.current = null;
    startConnection(cameraId, isArchiveMode, isArchiveMode ? positionMs : null);
  };

  return (
    <div className={`relative bg-black ${className}`} style={{ zIndex: 1 }}>
      <video
        ref={videoRef}
        muted={muted}
        playsInline
        autoPlay
        className="w-full h-full object-contain"
        style={{ position: "absolute", inset: 0 }}
        onClick={handleUserPlay}
      />


      {status === 'playing' && !needsInteraction && (
        <div className={`absolute top-2 left-2 px-2 py-1 ${isArchiveMode ? 'bg-orange-600/80' : 'bg-red-600/80'} text-white text-xs rounded flex items-center gap-1`}>
          <span className={`w-2 h-2 ${isArchiveMode ? 'bg-orange-300' : 'bg-red-300'} rounded-full animate-pulse`} />
          {isArchiveMode ? 'Archive' : 'LIVE'}
        </div>
      )}

      {needsInteraction && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50 cursor-pointer" onClick={handleUserPlay}>
          <div className="text-center text-white">
            <Play className="w-16 h-16 mx-auto mb-2 opacity-80" />
            <p>Click to play</p>
          </div>
        </div>
      )}

      {(status === 'connecting' || status === 'buffering') && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
          <div className="text-center text-white">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
            <p>{status === 'connecting' ? (isArchiveMode ? 'Loading archive...' : 'Connecting...') : 'Buffering...'}</p>
          </div>
        </div>
      )}

      {status === 'ended' && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80">
          <div className="text-center text-white">
            <AlertCircle className="w-8 h-8 mx-auto mb-2 text-orange-500" />
            <p className="mb-3">{errorMsg || 'Playback ended'}</p>
            <button onClick={retry} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg mx-auto">
              <RefreshCw className="w-4 h-4" /> Replay
            </button>
          </div>
        </div>
      )}

      {status === 'error' && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80">
          <div className="text-center text-white">
            <AlertCircle className="w-8 h-8 mx-auto mb-2 text-red-500" />
            <p className="mb-3">{errorMsg || 'Connection failed'}</p>
            <button onClick={retry} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg mx-auto">
              <RefreshCw className="w-4 h-4" /> Retry
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
